import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import type { Field } from '../types/Field';
import type { LoadedSchema, Schema } from '../types/Schema';
import type { SchemaSource } from '../types/SchemaSource';
import type { RemoveToast, ToastConfig } from '../model/toast';
import { TaskState } from './TaskState';
import type { WixPatternsContainer } from './WixPatternsContainer';

export interface SourceStateParams<T> {
  container: WixPatternsContainer;
  source: SchemaSource<T>;
}

/**
 * Holds a source's loaded schema, page-neutral: loads it once, exposes its
 * fields, and runs the source's field mutations with toasts and retry. Page
 * adapters (collection page, entity page) read from it.
 */
export class SourceState<T = Record<string, unknown>> {
  readonly container: WixPatternsContainer;
  readonly source: SchemaSource<T>;
  readonly makePrimaryTask = new TaskState();
  readonly duplicateTask = new TaskState();
  readonly duplicateWithContentTask = new TaskState();

  loadedSchema?: LoadedSchema<T> = undefined;

  constructor(params: SourceStateParams<T>) {
    this.container = params.container;
    this.source = params.source;

    makeObservable(this, {
      loadedSchema: observable.ref,
      schema: computed,
      fields: computed,
      mergeFields: action,
    });
  }

  get schema(): Schema | undefined {
    return this.loadedSchema?.schema;
  }

  get fieldActions() {
    return this.loadedSchema?.fieldActions;
  }

  get fields(): Field[] {
    const fields = this.schema?.fields ?? {};
    return Object.values(fields).filter((f): f is Field => Boolean(f));
  }

  /** Loads the schema. The consuming page adapter runs this, tracks its status,
   *  and shows a retry toast on failure. */
  load = async (): Promise<void> => {
    const loaded = await this.source.loadSchema();
    runInAction(() => {
      this.loadedSchema = loaded;
    });
  };

  /** Replaces the loaded schema's fields with the map returned by a mutation. */
  mergeFields(fields: Schema['fields']) {
    const current = this.loadedSchema;
    if (!current) {
      return;
    }
    const schema = { ...current.schema, fields };
    this.loadedSchema = {
      ...current,
      schema,
    };
    this.source.setLoadedSchema?.(schema);
  }

  makePrimary = (fieldId: string): void => {
    const run = this.fieldActions?.makePrimary;
    if (!run) {
      return;
    }
    void this.runFieldMutation(
      this.makePrimaryTask,
      () => run(fieldId),
      {
        successMessage: 'Primary field updated',
        successBi: 'cairo-field-action-make-primary-success',
        errorMessage: "Couldn't update primary field",
        errorBi: 'cairo-field-action-make-primary-error',
      },
      () => this.makePrimary(fieldId),
    );
  };

  duplicateField = (fieldId: string): void => {
    const run = this.fieldActions?.duplicateField;
    if (!run) {
      return;
    }
    void this.runFieldMutation(
      this.duplicateTask,
      () => run(fieldId),
      {
        successMessage: 'Field duplicated',
        successBi: 'cairo-field-action-duplicate-success',
        errorMessage: "Couldn't duplicate field",
        errorBi: 'cairo-field-action-duplicate-error',
      },
      () => this.duplicateField(fieldId),
    );
  };

  duplicateFieldWithContent = (fieldId: string): void => {
    const run = this.fieldActions?.duplicateFieldWithContent;
    if (!run) {
      return;
    }
    void this.runFieldMutation(
      this.duplicateWithContentTask,
      () => run(fieldId),
      {
        successMessage: 'Field duplicated with content',
        successBi: 'cairo-field-action-duplicate-with-content-success',
        errorMessage: "Couldn't duplicate field with content",
        errorBi: 'cairo-field-action-duplicate-with-content-error',
      },
      () => this.duplicateFieldWithContent(fieldId),
    );
  };

  // makePrimary/duplicate return the updated fields map (like add/edit/delete),
  // so merge it locally — same path as the modal actions, no full re-fetch.
  // Toasts + loading mirror the field-management actions, so the header and the
  // side panel report the same outcome.
  private runFieldMutation = async (
    task: TaskState<unknown>,
    run: () => Promise<Schema['fields']>,
    toasts: {
      successMessage: string;
      successBi: string;
      errorMessage: string;
      errorBi: string;
    },
    retry: () => void,
  ) => {
    if (task.status.isLoading) {
      return;
    }
    await task.run(async () => {
      const fields = await run();
      runInAction(() => this.mergeFields(fields));
    });
    if (task.status.isSuccess) {
      this.notify({
        type: 'SUCCESS',
        message: toasts.successMessage,
        biName: toasts.successBi,
      });
    } else if (task.status.isError) {
      this.notify({
        type: 'ERROR',
        message: toasts.errorMessage,
        biName: toasts.errorBi,
        action: {
          text: 'Try again',
          onClick: retry,
          removeToastOnClick: true,
        },
      });
    }
  };

  private currentToastRemove: RemoveToast | undefined;

  // Shared by the source's field mutations (make-primary / duplicate / delete) to
  // surface a toast; deduped against the previous one.
  notify = (config: ToastConfig) => {
    const show = this.container.showToast;
    if (!show) {
      return;
    }
    this.currentToastRemove?.();
    this.currentToastRemove = show(config).remove;
  };
}
